import React, { useState, useEffect, useRef, useCallback } from 'react';
import apiHelper from '../utils/apiHelper';
import LoadingSpinner from '../components/Common/LoadingSpinner';

import './Talents.css';

function Talents() {
    const [talentsData, setTalentsData] = useState([]);
    const [imageUrls, setImageUrls] = useState({});
    const [isLoading, setIsLoading] = useState(true);
    const [expandedIds, setExpandedIds] = useState(new Set());
    const [overflowingIds, setOverflowingIds] = useState(new Set());
    const textRefs = useRef({});

    const checkOverflows = useCallback(() => {
        const overflowing = new Set();
        Object.entries(textRefs.current).forEach(([id, el]) => {
            if (el && el.scrollHeight > el.clientHeight) {
                overflowing.add(id);
            }
        });
        setOverflowingIds(overflowing);
    }, []);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const data = await apiHelper.fetchTalents();
                const filtered = (data || []).filter(item => item.Title && item.Text && item.Link);
                setTalentsData(filtered);

                // Fetch images for each talent that has an Image field
                const urls = {};
                for (const item of filtered) {
                    if (item.Image) {
                        try {
                            const { fileContent: blob } = await apiHelper.fetchImageByName(item.Image);
                            if (blob) {
                                urls[item.ID] = URL.createObjectURL(blob);
                            }
                        } catch (imgErr) {
                            console.warn(`Failed to load image for ${item.Title}:`, imgErr.message);
                        }
                    }
                }
                setImageUrls(urls);
            } catch (err) {
                console.error('Error fetching talents:', err);
            } finally {
                setIsLoading(false);
            }
        };

        fetchData();

        return () => {
            // Revoke blob URLs on cleanup
            Object.values(imageUrls).forEach(url => URL.revokeObjectURL(url));
        };
    }, []);

    // Check overflows after data loads and on window resize
    useEffect(() => {
        if (!isLoading && talentsData.length > 0) {
            // Small delay to let the DOM render
            const timer = setTimeout(checkOverflows, 100);
            window.addEventListener('resize', checkOverflows);
            return () => {
                clearTimeout(timer);
                window.removeEventListener('resize', checkOverflows);
            };
        }
    }, [isLoading, talentsData, checkOverflows]);

    if (isLoading) {
        return (
            <div className="talents-page">
                <section className="wrapper">
                    <div className="container">
                        <LoadingSpinner text="Loading Skills & Training" />
                    </div>
                </section>
            </div>
        );
    }

    return (
        <div className="talents-page">
            <section className="wrapper">
                <div className="container">
                    {talentsData.length === 0 ? (
                        <p>No talents found.</p>
                    ) : (
                    <div className="talents-grid">
                        {talentsData.map((item) => (
                            <a
                                key={item.ID}
                                className={`talent-row ${expandedIds.has(item.ID) ? 'expanded' : ''}`}
                                href={item.Link}
                                target="_blank"
                                rel="noopener noreferrer"
                            >
                                <div className="infocardgrid-card talent-card">
                                    <div className="talent-image-wrapper">
                                        <img
                                            src={imageUrls[item.ID] || "/images/news-reading.jpg"}
                                            alt={item.Title}
                                            className="infocardgrid-image"
                                            onError={(e) => {
                                                if (e.target.src !== window.location.origin + "/images/news-reading.jpg") {
                                                    e.target.src = "/images/news-reading.jpg";
                                                }
                                            }}
                                        />
                                        <div className="talent-title-overlay">
                                            <h2 className="sub-header">{item.Title}</h2>
                                        </div>
                                    </div>
                                </div>
                                <div className="talent-text">
                                    <div
                                        className={`talent-text-content ${overflowingIds.has(item.ID) && !expandedIds.has(item.ID) ? 'truncated' : ''}`}
                                        ref={el => { textRefs.current[item.ID] = el; }}
                                    >
                                        <div className="summary" dangerouslySetInnerHTML={{ __html: item.Text }} />
                                    </div>
                                    <div className="talent-text-footer">
                                        {(overflowingIds.has(item.ID) || expandedIds.has(item.ID)) && (
                                            <button
                                                className="talent-view-more"
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    setExpandedIds(prev => {
                                                        const next = new Set(prev);
                                                        if (next.has(item.ID)) {
                                                            next.delete(item.ID);
                                                        } else {
                                                            next.add(item.ID);
                                                        }
                                                        return next;
                                                    });
                                                }}
                                            >
                                                {expandedIds.has(item.ID) ? 'View less' : 'View more'}
                                            </button>
                                        )}
                                        <span className="talent-external-link">
                                            <i className="fas fa-external-link-alt"></i> Visit resource
                                        </span>
                                    </div>
                                </div>
                            </a>
                        ))}
                    </div>
                    )}
                </div>
            </section>
        </div>
    );
}

export default Talents;
